import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useCloseAllCaseMutation,
	useGetFamilyMemberGroupListMutation,
} from '@/api/member-management/admissionRequest';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useGetDicListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppTextField from '@/common/components/form-inputs/AppTextField';

interface Props {
	open: boolean;
	onClose: () => void;
}

const NewCloseAllCaseDialog = (props: Props) => {
	const theme = useTheme();
	console.log(theme);
	const { open, onClose } = props;
	const { t } = useTranslation();
	const [familyMemberList, setFamilyMemberList] = useState<any>([]);
	const [openAssignProgramWorkers, setOpenAssignProgramWorkers] =
		useState(false);
	const [memberForm, setMemberForm] = useState({});
	const [getFamilyMemberGroupList] = useGetFamilyMemberGroupListMutation();
	const [closeAllCase] = useCloseAllCaseMutation();
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const memberType = i18n.t(
		location.pathname.includes('admission-request-list')
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);

	const userInfo = useSelector(selectMyInfo);

	const { data: closedReasonDicList } = useGetDicListQuery('closed_reason', {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		dateOfExitService: Yup.string()
			.required(t('errorMessage.required'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		dateOfCaseClosed: Yup.string()
			.required(t('errorMessage.required'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		closedReason: Yup.string().required(t('errorMessage.required')),
		closedReasonSpecify: Yup.string().when('closedReason', {
			is: (a: string) => a && closedReasonIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
	});
	const formSchema2 = Yup.object().shape({});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchClosedReason = methods.watch('closedReason');
	const methods2 = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema2),
	});

	const handleClose = () => {
		methods.reset({});
		onClose();
	};

	const handleCloseOtherDialog = () => {
		methods2.reset({});
		onClose();
	};

	const submitClose = async (data: any) => {
		const response = await closeAllCase({
			...data,
			id: admissionRequestId,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			handleCloseOtherDialog();
		}
	};

	const onNext: SubmitHandler<any> = async (data) => {
		setMemberForm(data);

		const response = await getFamilyMemberGroupList(admissionRequestId);
		if ('data' in response) {
			const list = response?.data?.data || [];
			setFamilyMemberList(list);
			// No associated members to remind about, close directly
			if (list.length === 0) {
				await submitClose(data);
				return;
			}
		}
		setOpenAssignProgramWorkers(true);
	};

	const onSubmit: SubmitHandler<any> = async () => {
		await submitClose(memberForm);
	};

	const columns: GridColDef[] = [
		{
			field: 'targetMemberNameEn',
			headerName: t('member.name'),
			flex: 10,
			renderCell: (params: any) => (
				<Box>
					<Typography variant='body2'>
						{params.row.targetMemberNameEn}
					</Typography>
					<Typography variant='body2'>
						{params.row.targetMemberNameZh}
					</Typography>
				</Box>
			),
		},
		{
			field: 'relationshipDesc',
			headerName: t('preAdmission.relationship'),
			flex: 10,
		},
		{
			field: 'targetFamilyMembership',
			headerName: t('member.membershipRole'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<a
					href={
						params.row.targetMemberIdDesc
							? `/member-management/member-list/detail/${params.row.targetMemberIdDesc}`
							: `/family-management/family-management-list/detail/${params.row.targetFamilyMemberIdDesc}`
					}
					target='_blank'
					rel='noopener noreferrer'
				>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</a>
			),
		},
	];

	const closedReasonIsOther = () => {
		const obj = closedReasonDicList?.data.find(
			(item: any) => item.id == watchClosedReason
		);
		if (obj && obj.code == '7e') return true;
		return false;
	};

	return (
		<>
			<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
				<DialogTitle variant='h5'>{t('button.closeAllCase')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.closeAllCaseText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onNext)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppDatePicker
										maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
										control={methods.control}
										name='dateOfExitService'
										label={t('member.dateExitService') + '*'}
									/>
								</Grid>
								<Grid size={12}>
									<AppDatePicker
										maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
										control={methods.control}
										name='dateOfCaseClosed'
										label={t('member.dateCaseClosed') + '*'}
									/>
								</Grid>
								<Grid size={12}>
									<AppDropDownMenu
										control={methods.control}
										name='closedReason'
										label={t('member.closedReason') + '*'}
										options={(closedReasonDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('closedReasonSpecify', '');
											methods.clearErrors('closedReasonSpecify');
										}}
									/>
								</Grid>
								<Grid size={12}>
									<AppTextField
										disabled={!closedReasonIsOther()}
										control={methods.control}
										name='closedReasonSpecify'
										label={`${t('member.closedReasonSpecify')}${
											closedReasonIsOther() ? '*' : ''
										}`}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onNext)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openAssignProgramWorkers}
				onClose={handleCloseOtherDialog}
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('member.closeAllProgramFamilyMemberTitle')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleCloseOtherDialog}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.closeAllProgramFamilyMemberText')}
					</Typography>
					<FormProvider {...methods2}>
						<form onSubmit={methods2.handleSubmit(onSubmit)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12} display='flex'>
									<LocalDataGrid
										rows={familyMemberList}
										columns={columns}
										getRowId={(row) => row.id}
										pagination={false}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ textTransform: 'none' }}
						onClick={() => handleCloseOtherDialog()}
					>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods2.handleSubmit(onSubmit)}
					>
						{t('button.ok')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewCloseAllCaseDialog;
